/**
 * duplicateSection — copy a section block, widgets and all, right below it.
 *
 * A section is a page-grid item (`contentType:"grid"`) whose widgets live in
 * a separate NESTED grid, so "duplicate" is two copies: the nested grid
 * (fresh id, every widget re-keyed, content props cloned) and the block
 * (fresh id, pointing at the new nested grid). Sections at/after the copy's
 * row shift down first — SHIFT BEFORE ADD, so compaction's (y,x) tie-break
 * can't reorder the copy past the moved ones (same rule insertSectionIntoGrid
 * follows).
 *
 * Persistence rides the existing reconciler: PageNode binds every nested grid
 * a page block references (the late-joiner binding pushes the seeded widgets
 * store→Yjs), exactly as it does for a template insert. Breakpoint overrides
 * (`::bp:` cells) are NOT copied — the copy follows the desktop layout.
 *
 * Plain helper: no-op (returns undefined) for a missing page/block or a block
 * that isn't a section.
 */
import { generateId, useGridStore } from "@fredericrous/grid";

import { SECTION_NESTED_CONFIG } from "./sectionInsert.js";
import { orderedSections } from "./sectionOrder.js";

export function duplicateSection(
  pageGridId: string,
  itemId: string,
): string | undefined {
  const store = useGridStore.getState();
  const page = store.grids[pageGridId];
  const block = page?.items[itemId];
  if (!page || !block || block.contentType !== "grid") return undefined;

  // 1. The nested grid, deep: same config, every widget re-keyed — and any
  //    Inline among them copied whole under its own fresh grid.
  const newGridId = copyNestedGrid(
    store,
    block.nestedGridId ??
      (block.contentProps?.["nestedGridId"] as string | undefined),
    "section",
  );

  // 2. Make room: every section below the source moves down by its height.
  const h = block.position.h;
  const targetY = block.position.y + h;
  for (const sec of orderedSections(page.items)) {
    if (sec.position.y >= targetY) {
      store.updateItem(pageGridId, sec.id, {
        position: { ...sec.position, y: sec.position.y + h },
      });
    }
  }

  // 3. The block itself, right below the source.
  return store.addItem(pageGridId, {
    position: { ...block.position, y: targetY },
    constraints: { ...block.constraints },
    contentType: "grid",
    nestedGridId: newGridId,
    contentProps: {
      ...(block.contentProps ?? {}),
      ...freshForm(block.contentProps?.["form"]),
      nestedGridId: newGridId,
    },
  });
}

/**
 * Copy a nested grid and everything under it, at every depth: the grid gets a
 * fresh id, each item a fresh id and cloned props, and an item that hosts a
 * grid of its own (an Inline) points at a fresh copy of THAT grid. Returns the
 * new grid's id.
 */
function copyNestedGrid(
  store: ReturnType<typeof useGridStore.getState>,
  srcGridId: string | undefined,
  idPrefix: string,
): string {
  const src = srcGridId ? store.exportLayout(srcGridId) : null;
  const newGridId = generateId(idPrefix);
  store.importLayout(newGridId, {
    config: src ? src.config : SECTION_NESTED_CONFIG,
    items: (src?.items ?? []).map((it) => {
      const copy = {
        ...it,
        id: generateId("w"),
        contentProps: { ...(it.contentProps ?? {}) },
      };
      const inner =
        it.nestedGridId ??
        (it.contentProps?.["nestedGridId"] as string | undefined);
      if (!inner) return copy;
      const innerCopy = copyNestedGrid(store, inner, "inline");
      copy.nestedGridId = innerCopy;
      copy.contentProps["nestedGridId"] = innerCopy;
      return copy;
    }),
  });
  return newGridId;
}

/**
 * duplicateInline — copy an Inline (and the blocks in its row) right below it
 * in its section grid. The same deep copy a section gets, so the copy is an
 * independent Inline: its own grid, its own children.
 */
export function duplicateInline(
  gridId: string,
  itemId: string,
): string | undefined {
  const store = useGridStore.getState();
  const block = store.grids[gridId]?.items[itemId];
  if (!block || block.contentType !== "inline-grid") return undefined;
  const newGridId = copyNestedGrid(
    store,
    block.nestedGridId ??
      (block.contentProps?.["nestedGridId"] as string | undefined),
    "inline",
  );
  return store.addItem(gridId, {
    position: { ...block.position, y: block.position.y + block.position.h },
    constraints: { ...block.constraints },
    contentType: "inline-grid",
    nestedGridId: newGridId,
    contentProps: { ...(block.contentProps ?? {}), nestedGridId: newGridId },
  });
}

/** A copied form section is a different form: same action, its own id. */
function freshForm(form: unknown): { form?: unknown } {
  if (!form || typeof form !== "object") return {};
  return { form: { ...form, id: generateId("form") } };
}
